import { useRef, useCallback, useState, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, Paperclip, Archive, Trash2, Inbox, MoreVertical, Mail, BookmarkX, Filter, BellOff, Forward, Zap } from 'lucide-react'
import { formatDistanceToNow, format, isToday, isYesterday, isThisWeek, parseISO } from 'date-fns'
import { useMailStore } from '../../stores/mailStore'
import { useUIStore } from '../../stores/uiStore'
import { useTrashThread } from '../../hooks/useTrashThread'
import { useRestoreThread } from '../../hooks/useRestoreThread'
import { useMarkThreadRead } from '../../hooks/useMarkThreadRead'
import { useStarThread } from '../../hooks/useStarThread'
import { useMarkImportant } from '../../hooks/useMarkImportant'
import { DropdownMenu, DmItem, DmDivider } from '../ui/DropdownMenu'
import type { Thread } from '../../lib/types'
import { clsx } from 'clsx'

// ─── Date formatting ──────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const date = parseISO(dateStr)
  if (isToday(date)) {
    // e.g. "9:04 AM"
    return format(date, 'h:mm a')
  }
  if (isYesterday(date)) return 'Yesterday'
  if (isThisWeek(date)) return format(date, 'EEE')
  return format(date, 'MMM d')
}

function getGroupLabel(dateStr: string): string {
  const date = parseISO(dateStr)
  if (isToday(date)) return 'Today'
  if (isYesterday(date)) return 'Yesterday'
  if (isThisWeek(date)) return 'This week'
  return format(date, 'MMMM yyyy')
}

// ─── Gravatar hook ────────────────────────────────────────────────────────────

function useGravatar(email: string): string | null {
  const [gravatarUrl, setGravatarUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!email) return
    const normalized = email.trim().toLowerCase()
    const encoder = new TextEncoder()
    const data = encoder.encode(normalized)
    crypto.subtle.digest('SHA-256', data).then((hashBuffer) => {
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hexHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
      setGravatarUrl(`https://www.gravatar.com/avatar/${hexHash}?s=92&d=mp`)
    })
  }, [email])

  return gravatarUrl
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#E07040', '#5B9FE8', '#6DB87A', '#A855F7',
  '#F59E0B', '#E85454', '#06B6D4', '#EC4899',
  '#14B8A6', '#F43F5E', '#8B5CF6', '#0EA5E9',
]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return (name[0] ?? '?').toUpperCase()
}

interface AvatarProps {
  name: string
  size?: number
  selected?: boolean
  email?: string
}

function ThreadAvatar({ name, size = 46, selected, email }: AvatarProps) {
  const color = getAvatarColor(name)
  const initials = getInitials(name)
  const gravatarUrl = useGravatar(email ?? '')
  const [imgFailed, setImgFailed] = useState(false)

  if (selected) {
    // Checked state: brand color circle with checkmark
    return (
      <div
        className="t-avatar t-avatar--selected"
        style={{ width: size, height: size, minWidth: size }}
        aria-hidden
      >
        <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
          <path d="M4 11.5l5 5 9-9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    )
  }

  if (email && gravatarUrl && !imgFailed) {
    return (
      <img
        src={gravatarUrl}
        alt={name}
        width={size}
        height={size}
        style={{ borderRadius: '50%', objectFit: 'cover', minWidth: size }}
        onError={() => setImgFailed(true)}
        aria-hidden
      />
    )
  }

  return (
    <div
      className="t-avatar"
      style={{ width: size, height: size, minWidth: size, background: color, fontSize: size * 0.37 }}
      aria-hidden
    >
      {initials}
    </div>
  )
}

// ─── Thread Row ───────────────────────────────────────────────────────────────

interface ThreadRowProps {
  thread: Thread
  isSelected: boolean
  isActive: boolean
  onSelect: (e: React.MouseEvent) => void
  onClick: () => void
  onFilterLike: (senderEmail: string, subject: string) => void
}

function ThreadRow({ thread, isSelected, isActive, onSelect, onClick, onFilterLike }: ThreadRowProps) {
  const { archiveThread, activeFolder } = useMailStore()
  const { addToast } = useUIStore()
  const trashThread = useTrashThread()
  const restoreThread = useRestoreThread()
  const markRead = useMarkThreadRead()
  const starThread = useStarThread()
  const markImportant = useMarkImportant()
  const isTrash = activeFolder === 'trash'
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [hovered, setHovered] = useState(false)

  const isUnread = thread.unread_count > 0

  // Derive sender display name from participants
  const rawSender = thread.participants[0] ?? 'Unknown'
  // Use the part before @ and capitalise first letter; keep full name if no @
  const senderBase = rawSender.includes('@')
    ? rawSender.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : rawSender
  const displayName = thread.message_count > 1
    ? `${senderBase}  ${thread.message_count}`
    : senderBase

  const dateStr = formatDate(thread.last_message_at)

  return (
    <motion.div
      className={clsx('tr', {
        'tr--unread':    isUnread,
        'tr--selected':  isSelected,
        'tr--active':    isActive,
        'tr--important': thread.is_important,
      })}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.12 }}
      draggable
    >
      {/* Avatar / checkbox (avatar turns into checkmark on hover) */}
      <div
        className="tr__avatar-wrap"
        onClick={(e) => { e.stopPropagation(); onSelect(e) }}
        title={isSelected ? 'Deselect' : 'Select'}
      >
        <ThreadAvatar
          name={rawSender}
          size={46}
          selected={isSelected}
          email={!isSelected ? (thread.participants[0] ?? undefined) : undefined}
        />
      </div>

      {/* Main content */}
      <div className="tr__body">

        {/* Row 1: sender name + message count · date · unread dot */}
        <div className="tr__row1">
          <span className="tr__sender">{displayName}</span>
          <div className="tr__time-group">
            <span className="tr__date">{dateStr}</span>
            <span className={clsx('tr__dot', { 'tr__dot--visible': isUnread })} aria-hidden />
          </div>
        </div>

        {/* Row 2: subject */}
        <div className="tr__row2">
          {thread.is_important && (
            <Zap size={12} className="tr__priority-icon" aria-label="Important" fill="currentColor" />
          )}
          <span className="tr__subject">{thread.subject || '(no subject)'}</span>
          {thread.has_attachments && (
            <Paperclip size={12} className="tr__paperclip" aria-label="Has attachment" />
          )}
        </div>

        {/* Row 3: snippet only (star moved to always-visible slot at far right) */}
        <div className="tr__row3">
          <span className="tr__snippet">{thread.snippet}</span>
        </div>
      </div>

      {/* Star — always visible at far right, never covered by hover actions */}
      <button
        className={clsx('tr__star', { 'tr__star--active': thread.is_starred })}
        onClick={(e) => { e.stopPropagation(); starThread(thread.id, !thread.is_starred) }}
        title={thread.is_starred ? 'Remove star' : 'Star'}
        aria-label={thread.is_starred ? 'Remove star' : 'Star'}
      >
        <Star
          size={18}
          fill={thread.is_starred ? 'var(--star-color)' : 'none'}
          stroke={thread.is_starred ? 'var(--star-color)' : 'currentColor'}
        />
      </button>

      {/* Hover action strip — slides in from right, stops before the star */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            className="tr__hover-actions"
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isTrash ? (
              <button
                className="btn-icon tr__ha-btn"
                onClick={() => { restoreThread(thread); addToast('Moved to Inbox', 'success', { duration: 2500 }) }}
                title="Move to Inbox"
              >
                <Inbox size={16} />
              </button>
            ) : (
              <>
                <button
                  className="btn-icon tr__ha-btn"
                  onClick={() => { archiveThread(thread.id); addToast('Archived', 'success', { duration: 2500 }) }}
                  title="Archive (e)"
                >
                  <Archive size={16} />
                </button>
                <button
                  className="btn-icon tr__ha-btn"
                  onClick={() => { trashThread(thread); addToast('Moved to Trash', 'info', { duration: 2500 }) }}
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
            <button
              className="btn-icon tr__ha-btn"
              onClick={(e) => { e.stopPropagation(); setMenuAnchor(menuAnchor ? null : e.currentTarget) }}
              title="More"
            >
              <MoreVertical size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Context menu */}
      <DropdownMenu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        width={224}
        align="right"
      >
        <DmItem icon={Mail}      label="Mark as unread"         onClick={() => { markRead(thread.id, false); addToast('Marked as unread', 'info'); setMenuAnchor(null) }} />
        <DmItem icon={BookmarkX} label="Mark as not important"  onClick={() => { markImportant(thread.id, false); setMenuAnchor(null) }} />
        <DmItem icon={Star}      label={thread.is_starred ? 'Remove star' : 'Add star'} onClick={() => { starThread(thread.id, !thread.is_starred); setMenuAnchor(null) }} />
        <DmDivider />
        <DmItem icon={Filter}    label="Filter messages like these" onClick={() => { onFilterLike(thread.participants[0] ?? '', thread.subject); setMenuAnchor(null) }} />
        <DmItem icon={BellOff}   label="Mute"                   onClick={() => { addToast('Thread muted', 'info'); setMenuAnchor(null) }} />
        <DmItem icon={Forward}   label="Forward as attachment"  onClick={() => { addToast('Forward as attachment — coming soon', 'info'); setMenuAnchor(null) }} />
      </DropdownMenu>
    </motion.div>
  )
}

// ─── Group Header ─────────────────────────────────────────────────────────────

function GroupHeader({ label }: { label: string }) {
  return <div className="thread-group-header">{label}</div>
}

// ─── Main List ────────────────────────────────────────────────────────────────

interface MessageListProps {
  threads: Thread[]
}

export function MessageList({ threads }: MessageListProps) {
  const {
    activeThreadId,
    selectedThreadIds,
    setActiveThread,
    toggleSelectThread,
    selectAll,
    clearSelection,
  } = useMailStore()

  const { openFilterCreator } = useUIStore()
  const parentRef = useRef<HTMLDivElement>(null)

  type ListItem =
    | { kind: 'header'; label: string }
    | { kind: 'thread'; thread: Thread }

  const items: ListItem[] = []
  let lastGroup = ''
  for (const thread of threads) {
    const group = getGroupLabel(thread.last_message_at)
    if (group !== lastGroup) {
      items.push({ kind: 'header', label: group })
      lastGroup = group
    }
    items.push({ kind: 'thread', thread })
  }

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => (items[i].kind === 'header' ? 32 : 90),
    overscan: 10,
  })

  const handleThreadClick = useCallback((thread: Thread) => {
    setActiveThread(thread.id)
  }, [setActiveThread])

  const handleSelect = useCallback((e: React.MouseEvent, threadId: string) => {
    toggleSelectThread(threadId)
  }, [toggleSelectThread])

  const hasSelection = selectedThreadIds.size > 0

  return (
    <div className="message-list-container">
      {/* Toolbar */}
      <div className="message-list-toolbar">
        <div className="message-list-toolbar__left">
          <button
            className={clsx('tr__checkbox-bare', { 'tr__checkbox-bare--checked': hasSelection })}
            onClick={() => hasSelection ? clearSelection() : selectAll()}
            title={hasSelection ? 'Deselect all' : 'Select all'}
          />
          <AnimatePresence>
            {hasSelection && (
              <motion.div
                className="message-list-toolbar__bulk"
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -4 }}
              >
                <span className="text-sm text-muted">{selectedThreadIds.size} selected</span>
                <button className="btn btn-ghost btn-sm">Archive</button>
                <button className="btn btn-ghost btn-sm">Delete</button>
                <button className="btn btn-ghost btn-sm">Mark read</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="message-list-toolbar__right">
          <span className="text-xs text-muted">{threads.length}</span>
        </div>
      </div>

      {/* Virtual scroll */}
      <div ref={parentRef} className="message-list-scroll">
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
          {virtualizer.getVirtualItems().map((vItem) => {
            const item = items[vItem.index]
            return (
              <div
                key={vItem.key}
                data-index={vItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vItem.start}px)`,
                }}
              >
                {item.kind === 'header' ? (
                  <GroupHeader label={item.label} />
                ) : (
                  <ThreadRow
                    thread={item.thread}
                    isSelected={selectedThreadIds.has(item.thread.id)}
                    isActive={activeThreadId === item.thread.id}
                    onSelect={(e) => handleSelect(e, item.thread.id)}
                    onClick={() => handleThreadClick(item.thread)}
                    onFilterLike={(email, subject) => openFilterCreator({ from: email, subject })}
                  />
                )}
              </div>
            )
          })}
        </div>

        {threads.length === 0 && (
          <div className="message-list-empty">
            <Inbox size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p>No messages</p>
          </div>
        )}
      </div>

      <style>{`
        /* ══════════════════════════════════════════════
           Message list shell
        ══════════════════════════════════════════════ */

        .message-list-container {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: var(--list-width);
          flex-shrink: 0;
          background: var(--bg-panel);
          border-right: 1px solid var(--border);
        }

        .message-list-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 14px;
          border-bottom: 1px solid var(--border);
          min-height: 44px;
          gap: 10px;
          flex-shrink: 0;
        }

        .message-list-toolbar__left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .message-list-toolbar__bulk {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .message-list-toolbar__right {
          display: flex;
          align-items: center;
        }

        .message-list-scroll {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .thread-group-header {
          padding: 8px 16px 4px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--text-tertiary);
          background: var(--bg-panel);
          user-select: none;
        }

        .message-list-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 40px;
          color: var(--text-tertiary);
          font-size: 13px;
        }

        /* ── bare checkbox (toolbar) ── */
        .tr__checkbox-bare {
          width: 16px;
          height: 16px;
          border-radius: 3px;
          border: 1.5px solid var(--border-strong);
          background: transparent;
          cursor: pointer;
          flex-shrink: 0;
          transition: all var(--duration-fast);
        }
        .tr__checkbox-bare--checked {
          background: var(--brand);
          border-color: var(--brand);
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2 6l3 3 5-5' stroke='white' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }

        /* ══════════════════════════════════════════════
           Avatar
        ══════════════════════════════════════════════ */

        .t-avatar {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: #fff;
          flex-shrink: 0;
          user-select: none;
          letter-spacing: 0.01em;
        }

        .t-avatar--selected {
          background: var(--brand) !important;
        }

        /* ══════════════════════════════════════════════
           Thread row (Gmail-mobile style)
        ══════════════════════════════════════════════ */

        .tr {
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 90px;
          padding: 10px 14px 10px 12px;
          cursor: pointer;
          border-bottom: 1px solid var(--border);
          position: relative;
          transition: background var(--duration-fast);
          overflow: hidden;
        }

        .tr:hover                { background: var(--bg-hover); }
        .tr--active              { background: var(--bg-selected) !important; }
        .tr--selected            { background: var(--bg-selected-strong) !important; }

        /* ── Avatar wrapper ── */
        .tr__avatar-wrap {
          flex-shrink: 0;
          cursor: pointer;
          border-radius: 50%;
          transition: opacity var(--duration-fast);
        }
        .tr__avatar-wrap:hover { opacity: 0.85; }

        /* ── Main body (fills remaining width) ── */
        .tr__body {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        /* ── Row 1: sender + date/dot ── */
        .tr__row1 {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .tr__sender {
          font-size: 14.5px;
          font-weight: 400;
          color: var(--text-secondary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
          letter-spacing: -0.01em;
        }

        .tr--unread .tr__sender {
          font-weight: 700;
          color: var(--text-primary);
        }

        .tr__time-group {
          display: flex;
          align-items: center;
          gap: 5px;
          flex-shrink: 0;
        }

        .tr__date {
          font-size: 12px;
          color: var(--text-tertiary);
          white-space: nowrap;
          font-family: var(--font-mono);
        }

        .tr--unread .tr__date {
          color: var(--text-secondary);
          font-weight: 600;
        }

        /* Unread blue dot */
        .tr__dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: var(--brand);
          flex-shrink: 0;
          opacity: 0;
          transform: scale(0.6);
          transition: opacity var(--duration-fast), transform var(--duration-fast);
        }

        .tr__dot--visible {
          opacity: 1;
          transform: scale(1);
        }

        /* ── Row 2: subject ── */
        .tr__row2 {
          display: flex;
          align-items: center;
          gap: 6px;
          overflow: hidden;
        }

        .tr__subject {
          font-size: 13.5px;
          font-weight: 400;
          color: var(--text-secondary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
        }

        .tr--unread .tr__subject {
          font-weight: 600;
          color: var(--text-primary);
        }

        .tr__paperclip {
          color: var(--text-tertiary);
          flex-shrink: 0;
        }

        .tr__priority-icon {
          color: #F59E0B;
          flex-shrink: 0;
        }

        .tr--important .tr__subject {
          color: var(--text-primary);
        }

        .tr--important {
          border-left: 3px solid #F59E0B;
          padding-left: 9px;
        }

        /* ── Row 3: snippet + star ── */
        .tr__row3 {
          display: flex;
          align-items: center;
          gap: 8px;
          /* leave room for the always-visible star (36px) on the right */
          padding-right: 36px;
        }

        .tr__snippet {
          font-size: 12.5px;
          color: var(--text-tertiary);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
          line-height: 1.4;
        }

        /* Star: always visible, absolutely anchored to the far right */
        .tr__star {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          color: var(--text-tertiary);
          cursor: pointer;
          border-radius: 50%;
          transition: color var(--duration-fast), background var(--duration-fast);
          padding: 0;
          z-index: 2;
        }
        .tr__star:hover {
          color: var(--star-color);
          background: var(--bg-elevated);
        }
        .tr__star--active {
          color: var(--star-color);
        }

        /* ── Hover actions overlay — stops before the star ── */
        .tr__hover-actions {
          position: absolute;
          right: 40px;   /* 10px star offset + 28px star + 2px gap */
          top: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          gap: 2px;
          padding: 0 0 0 24px;
          background: linear-gradient(
            to right,
            transparent,
            var(--bg-hover) 30%,
            var(--bg-hover)
          );
        }

        .tr--active .tr__hover-actions {
          background: linear-gradient(
            to right,
            transparent,
            var(--bg-selected) 30%,
            var(--bg-selected)
          );
        }

        .tr__ha-btn {
          width: 32px;
          height: 32px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary);
          border-radius: 50%;
        }
        .tr__ha-btn:hover {
          background: var(--bg-elevated);
          color: var(--text-primary);
        }
      `}</style>
    </div>
  )
}
